/**
 * What an agent sees when a message failed. **INBOX scope item 12, rule 4.**
 *
 * ┌─ ⛔ BOTH LAYERS, ALWAYS, AND THE DIAGNOSTIC IS NOT BEHIND A DISCLOSURE ─────┐
 * │ The build plan requires *"EVERY ERROR CARRIES THE META ERROR CODE AND ITS   │
 * │ MEANING, not just a friendly message"*, so every case returns an AGENT LINE │
 * │ and a DIAGNOSTIC LINE.                                                      │
 * │                                                                            │
 * │ ⚠️ THE ALWAYS-VISIBLE PART IS AN INHERITED CONSTRAINT, NOT A UI PREFERENCE. │
 * │ **Nothing alerts on an `error` log line in this system**, so the agent's    │
 * │ screen is the only place a refusal surfaces at all. A disclosure hides the  │
 * │ one copy. It can become one later, when something watches.                  │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ── ⛔ THE VOCABULARY IS THE DATABASE'S, AND THIS FILE MUST COVER ALL OF IT ──
 *
 * `messages_error_code_check` (migrations 0010, 0011) admits four families and
 * two open patterns. **The two patterns are why `renderMessageError` can never
 * be a lookup table**: `http_503` and `131047` are values nobody enumerated, and
 * a map would return undefined for the codes most likely to be new.
 *
 * `tests/message-error.test.ts` reads the CHECK **out of the live database** and
 * asserts every enumerated code has both lines - so a code added to the schema
 * without a render fails there rather than rendering blank on an agent's screen.
 *
 * ── ⚠️ THE WRITE HALF UNDER-DELIVERS AND THAT IS RECORDED, NOT HIDDEN ───────
 *
 * `outbound.ts` collapses SIX channel states into `channel_needs_reauth`, so an
 * agent whose channel was NEVER connected is told to re-authorise something that
 * has never been authorised. Rule 4 records this against the write side:
 * *"THE RULE IS RIGHT AND THE CODE UNDER-DELIVERS."* **This module cannot fix
 * it** - the distinction is gone by the time the row is written - and the wording
 * below is chosen so it is not actively wrong for the never-connected case.
 */

export type RetryOffer =
  /** Retry is offered. We know the failure is transient. */
  | { kind: 'retry' }
  /**
   * ⛔ A TEMPLATE, NOT A RETRY. The 24-hour window is closed, so resending the
   * same free-form message would fail identically. Offering "retry" here would
   * be an instruction that cannot work.
   */
  | { kind: 'template' }
  /**
   * Hidden. ⚠️ **Offering retry asserts we know retrying is safe**, and for a
   * code we cannot classify we do not know that. Absence is the honest answer.
   */
  | { kind: 'none' }

export type MessageError = {
  /** Plain language, for the person answering the customer. */
  agent: string
  /**
   * The code and its meaning, always rendered. **Never the word "unknown"** -
   * see the `meta_unrecognised_on_event` case.
   */
  diagnostic: string
  retry: RetryOffer
}

/**
 * ┌─ ⛔ THE SAME META FAILURE REACHES THIS COLUMN IN TWO DIFFERENT FORMS, AND A ┐
 * │ RENDERER KEYED ON ONE MISSES THE OTHER. Found 2026-08-08 by the            │
 * │ vocabulary test, which read the CHECK and reported SEVEN codes with no      │
 * │ render - all of them template failures.                                     │
 * │                                                                            │
 * │   `send.ts`    maps the number through `META_ERROR_NAMES` and writes the    │
 * │                **NAME**: `template_not_found`.                              │
 * │   `status.ts`  writes what `errorCodeForMessage` returns, which is the      │
 * │                **RAW NUMBER**: `132001`.                                    │
 * │                                                                            │
 * │ **Both are admitted by `messages_error_code_check` and both are correct.**  │
 * │ A send-time template rejection and a receipt-time one describe the same     │
 * │ Meta failure and land in the column spelled differently, because they       │
 * │ arrive through paths written months apart for different reasons.            │
 * │                                                                            │
 * │ ⚠️ SO THE MAP IS KEYED BY BOTH, and the numeric keys are NOT dead weight:   │
 * │ the receipt path writes them today. Keying by number alone - which is what  │
 * │ this file did first, by copying `META_ERROR_NAMES` - left the seven names   │
 * │ falling through to "no meaning is recorded for this code yet", which would  │
 * │ have been FALSE and on an agent's screen.                                   │
 * └────────────────────────────────────────────────────────────────────────────┘
 */
const META_NAMES: Readonly<Record<string, string>> = {
  '132000': 'Template parameter count mismatch',
  '132001': 'Template not found',
  '132005': 'Template text too long',
  '132007': 'Template format policy violation',
  '132012': 'Template parameter format mismatch',
  '132015': 'Template paused by Meta for quality',
  '132016': 'Template disabled by Meta',
  template_param_count_mismatch: 'Template parameter count mismatch',
  template_not_found: 'Template not found',
  template_text_too_long: 'Template text too long',
  template_format_policy_violation: 'Template format policy violation',
  template_param_format_mismatch: 'Template parameter format mismatch',
  template_paused: 'Template paused by Meta for quality',
  template_disabled: 'Template disabled by Meta',

  /**
   * ┌─ ⛔ [OBSERVED] 2026-08-11, AND IT IS THE ONLY ASYNCHRONOUS ONE ────────────┐
   * │ The first upload-related send failure this project has seen, captured as   │
   * │ `status-failed-131053-media-2026-08-11.json`. **Provenance is OBSERVED,    │
   * │ not DOC-SECONDARY** - it came off the wire with title, message and         │
   * │ `error_data.details`.                                                      │
   * │                                                                            │
   * │ ⛔ WHAT IT PROVES IS BIGGER THAN THE CODE: **META ACCEPTS A FILE AT UPLOAD  │
   * │ AND REJECTS IT AT SEND.** `/media` returned 200 and an id for a 20-byte     │
   * │ JPEG that is a header and an end marker with no image data. `/messages`     │
   * │ then returned 200 with a wamid, and the failure arrived ASYNCHRONOUSLY on   │
   * │ a status webhook. So the upload endpoint validates almost nothing and the   │
   * │ real check is at send - which is why a stored `meta_media_id` is a claim    │
   * │ Meta has not verified.                                                     │
   * │                                                                            │
   * │ ⚠️ AND "Media upload error" IS BOTH THE TITLE AND THE MESSAGE, AND SAYS     │
   * │ NOTHING. The actionable content is entirely in `error_data.details`, which  │
   * │ names the constraint: JPG/JPEG RGB/RGBA 8-bit, PNG, static WebP. That is    │
   * │ the same field that stated the 24-hour window rule in Meta's own words on   │
   * │ the 131047 capture, and it is the second time it has carried the whole      │
   * │ meaning - `thread-msg__error-diagnostic` renders it for exactly this.       │
   * └────────────────────────────────────────────────────────────────────────────┘
   */
  '131053': 'Media rejected by Meta at send time',
}

/**
 * ⛔ THE CODES THAT ARE RETRYABLE, AND IT IS A SHORT LIST ON PURPOSE.
 *
 * Only transport-level failures where the message never reached Meta's
 * decision. **A rejection is not retryable**: sending the identical message
 * again gets the identical rejection, and the button teaches an agent that
 * retrying is worth trying.
 */
const RETRYABLE = new Set(['transport', 'ambiguous_no_response'])

/**
 * Render a failure. **Total over the column's vocabulary**, including the two
 * open patterns.
 *
 * ⚠️ `null` IS NOT A FAILURE. A message with no error code has not failed, and
 * returning a `MessageError` for one would put a refusal under every bubble.
 */
export function renderMessageError(code: string | null): MessageError | null {
  if (code === null) return null

  switch (code) {
    case 'channel_setup_incomplete':
      return {
        agent: "WhatsApp isn't finished connecting yet. An admin needs to complete setup.",
        diagnostic: `${code}: the channel has not reached a connected state`,
        retry: { kind: 'none' },
      }

    case 'channel_awaiting_meta':
      return {
        agent: 'Waiting on Meta to verify this number. Nothing to do but wait.',
        /**
         * ⚠️ THE ONE STATE WHERE NOBODY CAN ACT AND THAT IS NORMAL. The
         * diagnostic says so, because an agent who reads "waiting" without
         * "this is expected" escalates it.
         */
        diagnostic: `${code}: Meta is verifying this number. No action is available to anyone.`,
        retry: { kind: 'none' },
      }

    case 'channel_needs_reauth':
      return {
        /**
         * ⚠️ WORDED FOR SIX STATES, NOT ONE. `outbound.ts` collapses
         * `not_started`, `provider_auth`, `selecting_account`, `number_pending`,
         * `verification_pending` and `granting` into this code, so "re-authorise
         * it" is wrong for a channel that was never authorised in the first
         * place. "needs attention" is true for all six.
         */
        agent: 'WhatsApp needs attention before messages can be sent. An admin has to finish or renew the connection.',
        diagnostic: `${code}: the channel is not connected. The exact step is on the admin channel page`,
        retry: { kind: 'none' },
      }

    case 'channel_disconnected':
      return {
        agent: 'This channel was disconnected.',
        diagnostic: `${code}: the channel connection was removed or revoked`,
        retry: { kind: 'none' },
      }

    case 'window_expired':
    case 'window_never_inbound':
      return {
        agent: 'The 24-hour window is closed. Only an approved template can be sent.',
        diagnostic:
          code === 'window_expired'
            ? `${code}: the customer's last inbound message is more than 24 hours old`
            : `${code}: this customer has never sent an inbound message`,
        // ⛔ A TEMPLATE, NOT A RETRY. See `RetryOffer`.
        retry: { kind: 'template' },
      }

    case 'no_access_token':
      return {
        agent: 'This channel has no credentials. An admin needs to reconnect it.',
        diagnostic: `${code}: no access token is configured for this phone number`,
        retry: { kind: 'none' },
      }

    case 'transport':
      return {
        agent: "WhatsApp couldn't be reached.",
        diagnostic: `${code}: the request to Meta failed before a response arrived`,
        retry: { kind: 'retry' },
      }

    case 'ambiguous_no_response':
      return {
        agent: "WhatsApp couldn't be reached, and it is not certain whether this was sent.",
        /**
         * ⛔ THE THIRD OUTCOME, AND THE WORDING MUST NOT COLLAPSE IT. The send
         * may have succeeded; we did not get an answer. An agent told simply
         * "failed" resends and the customer gets it twice.
         */
        diagnostic: `${code}: no response was received, so delivery is UNCONFIRMED rather than failed`,
        retry: { kind: 'retry' },
      }

    /**
     * ┌─ ⛔ THE ONLY CODE IN THIS MAP THAT IS OURS RATHER THAN META'S ───────────┐
     * │ Every other entry describes something WhatsApp did. This one says the    │
     * │ file the agent attached is no longer in our own store - a restore, a      │
     * │ purge, a bad key - discovered when the send tried to upload it.           │
     * │                                                                          │
     * │ ⚠️ SO THE AGENT LINE MUST NOT BLAME META, and getting that backwards is   │
     * │ the `channel_needs_reauth` mistake of 2026-08-02: a row telling somebody  │
     * │ to fix a credential that was never wrong. Here it would send them to      │
     * │ WhatsApp support for a file our disk lost.                                │
     * │                                                                          │
     * │ ⛔ AND THE RETRY OFFER IS `none`, WHICH IS NOT THE OBVIOUS ANSWER. Retry  │
     * │ is offered for transient failures, and a missing file is permanent as far │
     * │ as this row is concerned - the bytes are gone. **The action is to attach  │
     * │ it again**, which is a new message rather than a retry of this one, so    │
     * │ offering "retry" would be an instruction that cannot work. Same reasoning │
     * │ that gives `window_expired` a template offer instead of a retry.          │
     * └──────────────────────────────────────────────────────────────────────────┘
     */
    case 'media_missing_locally':
      return {
        agent:
          'This attachment is no longer available on our side, so it was not sent. Attach the file again to send it.',
        diagnostic: `${code}: the message descriptor says the file is stored, and the media store could not produce it`,
        retry: { kind: 'none' },
      }

    case 'meta_unrecognised_on_event':
      return {
        /**
         * ⛔ THIS MUST NOT RENDER AS "unknown error", AND THE REASON IS THAT THE
         * DETAIL IS NOT LOST. The raw code and the whole payload are on the
         * correlated `message_status_events` row, verbatim. "Unknown" discards
         * the only actionable content this case has.
         */
        agent:
          "WhatsApp refused this message and sent a reason we don't recognise. The raw code is recorded on this message's delivery events.",
        diagnostic: `${code}: see message_status_events for this message id, which holds the raw code and payload`,
        // Retry stays hidden: we do not know what the code meant, so we do not
        // know that retrying is safe.
        retry: { kind: 'none' },
      }

    default:
      break
  }

  if (code in META_NAMES) {
    return {
      agent: `${META_NAMES[code]!}. WhatsApp refused this message.`,
      diagnostic: `Meta error ${code}: ${META_NAMES[code]!}`,
      retry: { kind: 'none' },
    }
  }

  /**
   * ⛔ THE TWO OPEN PATTERNS, WHICH IS WHY THIS IS NOT A LOOKUP TABLE.
   *
   * `messages_error_code_check` admits `^http_[0-9]{3}$` and `^[0-9]+$` BY
   * SHAPE, deliberately, so a status or a Meta code added later is still
   * recordable. A map would return undefined for exactly the codes most likely
   * to be new - which is the moment the render matters most.
   */
  const http = /^http_([0-9]{3})$/.exec(code)
  if (http) {
    const status = Number(http[1])
    // 5xx and 429 are reachability failures; a 4xx is a rejection, and resending
    // the identical message gets the identical rejection.
    const retryable = status >= 500 || status === 429
    return {
      agent: retryable ? "WhatsApp couldn't be reached." : 'WhatsApp rejected this message.',
      diagnostic: `${code}: Meta responded with HTTP ${status}`,
      retry: retryable ? { kind: 'retry' } : { kind: 'none' },
    }
  }

  if (/^[0-9]+$/.test(code)) {
    return {
      agent: 'WhatsApp refused this message.',
      // ⚠️ THE CODE IS IN THE LINE even though nothing here names it. An unmapped
      // number is a one-line addition to `META_ERROR_NAMES` the next time anybody
      // looks, and it cannot be made if the number never reached a human.
      diagnostic: `Meta error ${code}: no meaning is recorded for this code yet`,
      retry: { kind: 'none' },
    }
  }

  /**
   * ⛔ A CODE THE DATABASE ADMITS AND THIS FILE DOES NOT KNOW.
   *
   * The CHECK is the authority and it can be widened without touching this
   * module. **Rendering nothing would drop a real failure off the screen**, so
   * the code itself is shown - ugly, readable, and impossible to confuse with a
   * message that succeeded. `tests/message-error.test.ts` reads the constraint
   * and fails when an enumerated code reaches this branch, so it stays reachable
   * only by the open patterns.
   */
  return {
    agent: 'This message failed to send.',
    diagnostic: `${code}: this code has no rendering yet. It is recorded on the message row.`,
    retry: { kind: 'none' },
  }
}

/**
 * ⛔ COMPOSER DISABLE IS UNIFORM ACROSS EVERY REFUSAL CODE, NOT PER-CODE.
 *
 * Per-code is a refinement that wants evidence from real use, and there is none:
 * **nobody has ever operated this product.** Uniform is the honest default and
 * is ONE decision to revisit rather than nine.
 *
 * ⚠️ Exported as a function rather than inlined so the day that evidence exists,
 * the change is here and every caller keeps working - the same seam argument as
 * `scopeFor`.
 */
export function blocksComposer(code: string | null): boolean {
  return renderMessageError(code) !== null
}
